import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Skeleton,
  Alert,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui'
import { CreditCard, Wallet, Receipt, RefreshCw, Trash2, Star, ExternalLink } from 'lucide-react'
import { useApiClient, api, type PaymentMethod, type Receipt as ReceiptType } from '@/api'

export function BillingPage() {
  const client = useApiClient()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [receipts, setReceipts] = useState<ReceiptType[]>([])
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [methodsRes, receiptsRes] = await Promise.all([
        api.getPaymentMethods(client),
        api.getReceipts(client, 10),
      ])
      setPaymentMethods(methodsRes.payment_methods)
      setReceipts(receiptsRes.receipts)
    } catch (err) {
      console.error('Failed to load billing data:', err)
      setError('Failed to load billing information')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleRemoveMethod = async (methodId: string) => {
    setActionLoading(methodId)
    try {
      await api.removePaymentMethod(client, methodId)
      setPaymentMethods((prev) => prev.filter((m) => m.id !== methodId))
    } catch (err) {
      console.error('Failed to remove payment method:', err)
      setError('Failed to remove payment method')
    } finally {
      setActionLoading(null)
    }
  }

  const handleSetDefault = async (methodId: string) => {
    setActionLoading(`default-${methodId}`)
    try {
      await api.setDefaultPaymentMethod(client, methodId)
      setPaymentMethods((prev) =>
        prev.map((m) => ({
          ...m,
          is_default: m.id === methodId,
        }))
      )
    } catch (err) {
      console.error('Failed to set default payment method:', err)
      setError('Failed to set default payment method')
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Billing"
          description="Manage your payment methods and receipts"
        />
        <div className="space-y-6">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error && paymentMethods.length === 0) {
    return (
      <div>
        <PageHeader
          title="Billing"
          description="Manage your payment methods and receipts"
        />
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-[var(--text-secondary)] mb-4">{error}</p>
            <Button onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Billing"
        description="Manage your payment methods and receipts"
      />

      {error && (
        <Alert variant="error" className="mb-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="space-y-6">
        {/* Payment Methods */}
        <Card>
          <CardHeader>
            <CardTitle>Payment Methods</CardTitle>
          </CardHeader>
          <CardContent>
            {paymentMethods.length === 0 ? (
              <div className="text-center py-6">
                <Wallet className="h-8 w-8 text-[var(--text-muted)] mx-auto mb-3" />
                <p className="text-sm text-[var(--text-secondary)]">
                  No payment methods added
                </p>
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  Add a payment method to enable purchases within apps
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-elevated-2)]"
                  >
                    <div className="flex items-center gap-3">
                      <PaymentMethodIcon processor={method.processor} brand={method.brand} />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[var(--text-primary)]">
                            {getPaymentMethodTitle(method)}
                          </p>
                          {method.is_default && (
                            <Badge variant="info" className="text-xs">Default</Badge>
                          )}
                          {method.status !== 'active' && (
                            <Badge variant={method.status === 'expired' ? 'default' : 'error'} className="text-xs">
                              {method.status}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-[var(--text-secondary)]">
                          {getPaymentMethodDetail(method)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!method.is_default && method.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(method.id)}
                          loading={actionLoading === `default-${method.id}`}
                          title="Set as default"
                        >
                          <Star className="h-4 w-4" />
                        </Button>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={actionLoading === method.id}
                            title="Remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove payment method?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will remove {getPaymentMethodTitle(method)} from your account.
                              You can add it back later.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleRemoveMethod(method.id)}>
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button variant="secondary" className="mt-4" disabled>
              Add payment method
            </Button>
            <p className="mt-2 text-xs text-[var(--text-muted)]">
              Payment methods are added through app checkout flows
            </p>
          </CardContent>
        </Card>

        {/* Recent Receipts */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Receipts</CardTitle>
              {receipts.length > 0 && (
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/billing/receipts">View all</Link>
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {receipts.length === 0 ? (
              <div className="text-center py-6">
                <Receipt className="h-8 w-8 text-[var(--text-muted)] mx-auto mb-3" />
                <p className="text-sm text-[var(--text-secondary)]">
                  No receipts yet
                </p>
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  Your purchase receipts will appear here
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--border-subtle)]">
                      <th className="text-left text-xs font-medium text-[var(--text-secondary)] pb-3">
                        Receipt
                      </th>
                      <th className="text-left text-xs font-medium text-[var(--text-secondary)] pb-3">
                        App
                      </th>
                      <th className="text-left text-xs font-medium text-[var(--text-secondary)] pb-3">
                        Amount
                      </th>
                      <th className="text-left text-xs font-medium text-[var(--text-secondary)] pb-3">
                        Date
                      </th>
                      <th className="text-right text-xs font-medium text-[var(--text-secondary)] pb-3">

                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {receipts.map((receipt) => (
                      <tr
                        key={receipt.id}
                        className="border-b border-[var(--border-subtle)] last:border-0"
                      >
                        <td className="py-4">
                          <p className="text-sm font-mono text-[var(--text-primary)]">
                            {receipt.receipt_number}
                          </p>
                          {receipt.description && (
                            <p className="text-xs text-[var(--text-muted)]">
                              {receipt.description}
                            </p>
                          )}
                        </td>
                        <td className="py-4 text-sm text-[var(--text-secondary)]">
                          {receipt.app_name || 'Unknown app'}
                        </td>
                        <td className="py-4">
                          <span className="text-sm font-medium text-[var(--text-primary)]">
                            {formatCurrency(receipt.amount_cents, receipt.currency)}
                          </span>
                        </td>
                        <td className="py-4 text-sm text-[var(--text-secondary)]">
                          {formatDate(receipt.issued_at)}
                        </td>
                        <td className="py-4 text-right">
                          {receipt.pdf_url && (
                            <Button variant="ghost" size="sm" asChild>
                              <a href={receipt.pdf_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function PaymentMethodIcon({ processor, brand }: { processor: string; brand: string | null }) {
  const iconClass = 'h-5 w-5 text-[var(--text-secondary)]'

  if (processor === 'paypal') {
    return <Wallet className={iconClass} />
  }

  // For cards, show card icon
  return <CreditCard className={iconClass} />
}

function getPaymentMethodTitle(method: PaymentMethod): string {
  if (method.processor === 'paypal') {
    return 'PayPal'
  }

  if (method.brand) {
    return `${method.brand.charAt(0).toUpperCase()}${method.brand.slice(1)}`
  }

  return method.display_name || 'Card'
}

function getPaymentMethodDetail(method: PaymentMethod): string {
  if (method.processor === 'paypal') {
    return method.payer_detail || method.payer_name || ''
  }

  const parts: string[] = []
  if (method.last_four) {
    parts.push(`•••• ${method.last_four}`)
  }
  if (method.expiry_month && method.expiry_year) {
    parts.push(`Exp ${method.expiry_month.toString().padStart(2, '0')}/${method.expiry_year.toString().slice(-2)}`)
  }
  return parts.join(' • ')
}

function formatCurrency(cents: number, currency: string): string {
  const amount = cents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount)
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  })
}

export { AppBillingPage } from './app-billing'
